import { Box, Container, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Scale, BookOpen, FileText } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const sections = [
  {
    icon: Scale,
    title: 'Legal Civil Works Disclaimer',
    content: `Clarence Homes is committed to providing accurate and reliable information. The explanations and information provided on this page are intended to offer general insights into legal disclaimers for civil works projects. However, it is important to note that this content should not be construed as legal advice or as a substitute for professional legal consultation. Every civil works project is unique, and the specific terms and conditions should be carefully tailored to address the individual requirements and legal considerations. We strongly advise seeking legal counsel to ensure that the Terms & Conditions accurately reflect the legal relationship between the company and its clients or stakeholders.`,
  },
  {
    icon: BookOpen,
    title: 'Understanding Legal Boundaries for Civil Works',
    content: `In the context of civil works projects, the Terms and Conditions ("T&C") serve as a crucial framework that defines the legal parameters governing the interactions and engagements related to the project. These provisions are essential for establishing the legal obligations and rights of all involved parties, including the company and its clients, subcontractors, and regulatory bodies. It is imperative to customize the T&C to align with the specific nature and scope of each civil works endeavor, taking into account factors such as project scope, contractual agreements, compliance requirements, and risk management.`,
  },
  {
    icon: FileText,
    title: 'Key Considerations for T&C in Civil Works',
    content: `When formulating Terms & Conditions for civil works projects, several critical aspects warrant attention. These may encompass delineating permissible usage of project-related information, outlining payment terms and methods, specifying provisions for potential modifications to the project scope, defining warranties or guarantees, addressing intellectual property rights, and outlining procedures for account suspension or termination. Given the distinct nature of civil works projects, it is essential to craft comprehensive and tailored Terms & Conditions that address the unique legal aspects and potential complexities inherent in such endeavors.`,
  },
];

function TermsAndConditions() {
  useEffect(() => {
    AOS.init({ duration: 800, once: false, offset: 100, easing: 'ease-out-cubic' });
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f8f8', overflow: 'hidden' }}>

      {/* Hero */}
      <Box sx={{
        backgroundColor: 'var(--primary-color)',
        py: { xs: 7, md: 10 },
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute',
          top: '-80px', right: '-80px',
          width: '320px', height: '320px',
          borderRadius: '50%',
          border: '70px solid rgba(192,174,93,0.07)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""', position: 'absolute',
          bottom: '-90px', left: '-50px',
          width: '280px', height: '280px',
          borderRadius: '50%',
          border: '55px solid rgba(192,174,93,0.05)',
          pointerEvents: 'none',
        },
      }}>
        <Typography sx={{ fontSize: { xs: '34px', md: '52px' }, fontWeight: 800, color: '#fff', letterSpacing: 2 }}>
          TERMS & CONDITIONS
        </Typography>
        <Box sx={{ width: '80px', height: '4px', backgroundColor: 'var(--primary-text-color)', mx: 'auto', mt: 3, mb: 2.5 }} />
        <Typography sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'rgba(255,255,255,0.65)', mt: 1, maxWidth: 560, mx: 'auto' }}>
          Please read these terms carefully before engaging with our services.
        </Typography>
      </Box>

      {/* Notice banner */}
      <Box sx={{ backgroundColor: 'var(--primary-text-color)', py: 2, px: 3 }}>
        <Container maxWidth="xl">
          <Typography sx={{ fontSize: '13px', color: 'var(--primary-color)', fontWeight: 600, textAlign: 'center' }}>
            This document is for informational purposes only and does not constitute legal advice. Please consult a qualified legal professional for guidance specific to your situation.
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>

        {/* Intro card */}
        <Box
          data-aos="fade-up"
          sx={{
            backgroundColor: 'var(--primary-color)',
            borderRadius: '12px',
            p: { xs: 3, md: 5 },
            mb: 6,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute', top: 0, left: 0,
              width: '6px', height: '100%',
              backgroundColor: 'var(--primary-text-color)',
            }
          }}
        >
          <Typography sx={{ fontSize: { xs: '20px', md: '26px' }, fontWeight: 800, color: '#fff', mb: 1.5 }}>
            Legal Framework Overview
          </Typography>
          <Typography sx={{ fontSize: '15px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.9 }}>
            The following Terms & Conditions govern all civil works projects undertaken by Clarence Homes. By engaging our services, you acknowledge and agree to be bound by these terms. These provisions have been established to protect the interests of all parties involved and to ensure the highest standards of professional conduct.
          </Typography>
        </Box>

        {/* Section cards */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Box
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 80}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  p: { xs: 3, md: 5 },
                  boxShadow: '0 2px 20px rgba(19,25,83,0.07)',
                  display: 'flex',
                  gap: { xs: 2, md: 4 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                    transform: 'translateY(-6px)',
                  }
                }}
              >
                {/* Icon badge */}
                <Box sx={{
                  flexShrink: 0,
                  width: 60, height: 60,
                  borderRadius: '10px',
                  backgroundColor: 'var(--primary-color)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={26} color="var(--primary-text-color)" />
                </Box>

                {/* Text */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: '4px', height: '22px', backgroundColor: 'var(--primary-text-color)', borderRadius: '2px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: 700, color: 'var(--primary-color)' }}>
                      {section.title}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '15px', color: '#444', lineHeight: 1.9 }}>
                    {section.content}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

      </Container>
    </Box>
  );
}

export default TermsAndConditions;
